import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Search, Layers, ChevronLeft } from 'lucide-react';

// Pick a module from another course and copy it (with all elements) into this course
export const ImportModuleDialog = ({ open, onOpenChange, currentCourseId, onImport }) => {
  const { api } = useAuth();
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState('');
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCourse(null);
    setQ('');
    api.get('/courses/admin/list?limit=200').then(r => setCourses((r.data?.courses || []).filter(c => c.id !== currentCourseId))).catch(() => setCourses([]));
  }, [open, api, currentCourseId]);

  const pickCourse = async (c) => {
    setCourse(c);
    setLoading(true);
    try {
      const r = await api.get(`/builder/courses/${c.id}`);
      setModules(r.data?.modules || []);
    } catch {
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  const doImport = async (m) => {
    setImporting(m.id);
    try { await onImport(m); onOpenChange(false); } finally { setImporting(null); }
  };

  const list = courses.filter(c => !q || c.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col" data-testid="import-module-dialog">
        <DialogHeader>
          <DialogTitle>{course ? `Modules in "${course.title}"` : 'Import a module from another course'}</DialogTitle>
          <DialogDescription>The module and all its elements are copied. The original course is not changed.</DialogDescription>
        </DialogHeader>
        {!course ? (
          <>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses..." className="pl-9" data-testid="import-course-search" />
            </div>
            <div className="flex-1 overflow-y-auto divide-y border rounded-lg min-h-[200px]">
              {list.map(c => (
                <button key={c.id} type="button" onClick={() => pickCourse(c)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50" data-testid={`import-course-${c.id}`}>
                  {c.title} <span className="text-xs text-slate-400">({c.status})</span>
                </button>
              ))}
              {!list.length && <p className="p-4 text-sm text-slate-500 text-center">No courses found</p>}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
            <Button variant="ghost" size="sm" onClick={() => setCourse(null)} data-testid="import-back-btn"><ChevronLeft className="h-4 w-4 mr-1" /> All courses</Button>
            {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#20acf6]" /></div> : modules.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-3 border rounded-lg" data-testid={`import-module-row-${m.id}`}>
                <Layers className="h-4 w-4 text-[#20acf6] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.title}</p>
                  <p className="text-xs text-slate-500">{(m.elements || []).length} elements</p>
                </div>
                <Button size="sm" disabled={!!importing} onClick={() => doImport(m)} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid={`import-module-btn-${m.id}`}>
                  {importing === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import'}
                </Button>
              </div>
            ))}
            {!loading && !modules.length && <p className="text-sm text-slate-500 text-center py-6">This course has no modules.</p>}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
